import {
  modalCopyState,
  modalDeleteState,
  modalOfferLightUpsellState
} from '../state/modals'
import { pageErrorState, pageState } from '../state/page'
import { resultsState, thumbnailState } from '../state/results'
import { DEFAULT_SEARCH, searchState } from '../state/search'

import { logAppError } from 'Helpers/logger'

import getIntl from 'Helpers/intl'

import { toastState } from '../../../../recoil'
import {
  checkTemplatesLimitsExceeded,
  copyTemplate,
  deleteTemplate,
  fetchTemplates,
  generateThumbnail,
  getTargetLists
} from '../actions/actions'

export const updateThumbCallback = ({ set, snapshot }) => async (
  idTemplate,
  payload
) => {
  const { items } = await snapshot.getPromise(resultsState)

  set(resultsState, prev => ({
    ...prev,
    items: prev.items.map(({ id, ...rest }) => ({
      id,
      ...rest,
      ...(id === idTemplate ? payload : {})
    }))
  }))
}

export const onThumbErrorCallback = ({ set, snapshot }) => async idTemplate => {
  const item = await snapshot.getPromise(thumbnailState(idTemplate))
  const {
    searchParams: { type }
  } = await snapshot.getPromise(resultsState)

  if (item) {
    if (item.retry > 0) {
      try {
        const newThumb = await generateThumbnail(type, idTemplate)

        set(thumbnailState(idTemplate), {
          ...item,
          loading: true,
          thumbnail: newThumb,
          retry: item.retry - 1
        })
      } catch (error) {
        logAppError({
          appError: error,
          Component: 'TemplatesList',
          Method: 'onThumbErrorCallback'
        })

        set(thumbnailState(idTemplate), {
          ...item,
          loading: false,
          error: true,
          retry: item.retry - 1
        })
      }
    } else {
      set(thumbnailState(idTemplate), {
        ...item,
        loading: false,
        error: true,
        retry: item.retry - 1
      })
    }
  }
}

export const fetchTemplatesCallback = ({ set, snapshot }) => async params => {
  set(pageState, prev => ({
    ...prev,
    fetchingResults: true
  }))

  const prevSearchState = await snapshot.getPromise(searchState)

  const searchParams = {
    ...prevSearchState,
    ...params
  }

  try {
    const results = await fetchTemplates(searchParams)

    set(searchState, searchParams)

    set(resultsState, {
      ...results,
      searchParams
    })
  } catch (error) {
    logAppError({ appError: error })
    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }

  set(pageState, prev => ({
    ...prev,
    fetchingResults: false
  }))
}

export const resetSearchCallback = ({
  set,
  reset,
  snapshot
}) => async params => {
  set(pageState, prev => ({
    ...prev,
    fetchingResults: true
  }))

  reset(searchState)

  try {
    const results = await fetchTemplates(DEFAULT_SEARCH)

    set(resultsState, {
      ...results,
      searchParams: DEFAULT_SEARCH
    })
  } catch (error) {
    logAppError({ appError: error })
    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }

  set(pageState, prev => ({
    ...prev,
    fetchingResults: false
  }))
}

export const modalCopyCallback = ({ set, snapshot }) => async id => {
  set(pageState, prev => ({
    ...prev,
    fetchingResults: true
  }))

  const { items } = await snapshot.getPromise(resultsState)
  const { title } = items.find(o => o.id === id)

  try {
    const lists = await getTargetLists()

    set(modalCopyState, {
      show: true,
      id,
      title,
      lists,
      copyToLists: [],
      selectAll: false
    })
  } catch (error) {
    logAppError({ appError: error })
    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }

  set(pageState, prev => ({
    ...prev,
    fetchingResults: false
  }))
}

export const modalCopyConfirmCallback = ({
  set,
  snapshot,
  reset
}) => async () => {
  set(pageState, prev => ({
    ...prev,
    fetchingResults: true
  }))
  const {
    id,
    title,
    selectAll,
    lists,
    copyToLists
  } = await snapshot.getPromise(modalCopyState)

  try {
    const targetList = selectAll ? lists.map(({ value }) => value) : copyToLists
    await copyTemplate(id, title, targetList)

    reset(modalCopyState)
    set(toastState, {
      message: getIntl().formatMessage({ id: 'Load.Bee.TemplateCopied' })
    })
  } catch (error) {
    logAppError({ appError: error })
    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }

  set(pageState, prev => ({
    ...prev,
    fetchingResults: false
  }))
}

export const modalDeleteCallback = ({ set }) => id => {
  set(modalDeleteState, {
    show: true,
    id
  })
}

export const modalDeleteConfirmCallback = ({
  set,
  snapshot,
  reset
}) => async () => {
  set(pageState, prev => ({
    ...prev,
    fetchingResults: true
  }))

  const searchParams = await snapshot.getPromise(searchState)

  const { id } = await snapshot.getPromise(modalDeleteState)

  try {
    await deleteTemplate(id)

    const results = await fetchTemplates(searchParams)

    set(resultsState, {
      ...results,
      searchParams
    })

    reset(modalDeleteState)

    set(toastState, {
      message: getIntl().formatMessage({ id: 'Load.Messages.TemplateDeleted' })
    })
  } catch (error) {
    logAppError({ appError: error })
    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }

  set(pageState, prev => ({
    ...prev,
    fetchingResults: false
  }))
}

export const canCopyTemplateCallback = ({ set }) => async id => {
  set(pageState, prev => ({
    ...prev,
    fetchingResults: true
  }))

  let canCopy = false

  try {
    const hasExceededTemplateLimits = await checkTemplatesLimitsExceeded()

    if (hasExceededTemplateLimits) {
      set(modalOfferLightUpsellState, { show: true })
    } else {
      canCopy = true
    }
  } catch (error) {
    logAppError({ appError: error })
    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }

  set(pageState, prev => ({
    ...prev,
    fetchingResults: false
  }))

  return canCopy
}
